import { Feature } from "../phonemes/features";
import { ipaToFeatures, IpaType } from "../phonemes/ipa";

export interface SonorityResults {
  /** sets of phonemes ordered from more to less sonorous */
  readonly ordered: IpaType[][];
  /** any phonemes that aren't measured by sonority, e.g., tones */
  readonly leftover: IpaType[];
  /** map category abbreviations (e.g., V or C) to segments */
  readonly cats: Record<string, IpaType[]>;
};

/**
 * order phonemes according to the sonority hierarchy,
 * useful for analyzing the ordering of sounds in a syllable.
 * @returns phonemes grouped by sonority + others
 */
export function orderBySonority(phonemes: IpaType[]): SonorityResults {
  const ordered: IpaType[][] = [];
  const leftover: IpaType[] = [];
  const cats: Record<string, IpaType[]> = {};
  sonorityHierarchy.forEach(_ => ordered.push([]));

  phonemes.forEach(p => {
    const level = lookupSonority(p);
    if (level !== false) {
      ordered[level].push(p);
      addToCats(p, level, cats);
    } else {
      leftover.push(p);
    }
  });
  return { ordered, leftover, cats };
}

/** return the sonority of a phoneme from high to low, or false if unknown */
export function getSonorityLevel(p: IpaType): [number, SyllableCats] | false {
  const index = lookupSonority(p);
  if (index === false) {
    return false;
  }
  return [sonorityHierarchy.length - index, syllableCat[index]];
}

/** return the sonority of a phoneme by looking up features in a list, or false if unknown */
function lookupSonority(p: IpaType): number | false {
  const features = ipaToFeatures(p);
  if (features === undefined) {
    return false;
  }
  for (let i = 0; i < sonorityHierarchy.length; i++) {
    const options = sonorityHierarchy[i];
    const includeEjective = options[0] === 'ejective';
    if (options.some(option => isMatch(features, option, includeEjective))) {
      return i;
    }
  }
  return false;
}

function isMatch(features: Feature[], option: Option, includeEjective: boolean): boolean {
  if (!includeEjective && features.includes('ejective')) {
    return false;
  }
  if (Array.isArray(option)) {
    return option.every(o => features.includes(o));
  } else {
    return features.includes(option);
  }
}

function addToCats(p: string, i: number, cats: Record<string, string[]>) {
  addToCat(p, sonorityCat1[i], cats);
  addToCat(p, sonorityCat2[i], cats);
  addToCat(p, sonorityCat3[i], cats);
  addToCat(p, sonorityCat4[i], cats);
}

function addToCat(p: string, cat: string, cats: Record<string, string[]>) {
  if (cats[cat] === undefined) {
    cats[cat] = [p];
  } else {
    cats[cat].push(p);
  }
}

/**
 * the sonority hierarchy provides a useful rule-of-thumb
 * for the ordering of sounds in a syllable
 */
const sonorityHierarchy: Option[][] = [
  ["open", "near-open"],
  ["mid", "open-mid", "close-mid"],
  ["close", "near-close"],
  [["syllabic", "consonant"]],
  ["approximant"],

  ["tap-flap", "trill"],
  ["lateral-approximant", "lateral-flap", "lateral-fricative"],
  ["click"],  // e.g., kǃ ŋǃ are more common than kǃx ɢǃqʰ
  ["nasal"],
  ["fricative"],
  ["affricate"],
  [["voiced", "plosive"]],
  [["voiceless", "plosive"]],

  // not officially part of the hierarchy
  ["implosive"],
  ["ejective"],
];
// reference: https://en.wikipedia.org/wiki/Sonority_hierarchy

type Option = Feature | Feature[];

/**
 * category abbreviation lists match order of sonorityHierarchy,
 * abbreviations are from PhonemeCat
 */
export const sonorityCat1 = ['V', 'V', 'V', 'V', 'C', 'C', 'C', 'C', 'C', 'C', 'C', 'C', 'C', 'C', 'C'];
export const sonorityCat2 = [' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', ' ', 'O', 'O', 'O', 'O', ' ', ' '];
export const sonorityCat3 = [' ', ' ', 'Y', ' ', 'Y', ' ', ' ', ' ', ' ', ' ', ' ', 'S', 'S', ' ', ' '];
export const sonorityCat4 = ['Ɑ', 'Ɛ', 'Ʊ', 'U', 'J', 'Ɽ', 'Ɬ', 'X', 'N', 'F', 'A', 'D', 'T', 'Ƃ', 'Ꜭ'];

type SyllableCats = 'V' | 'C' | 'X';  // vowel, consonant, click, modifier
const syllableCat: SyllableCats[] = ['V', 'V', 'V', 'V', 'C', 'C', 'C', 'X', 'C', 'C', 'C', 'C', 'C', 'C', 'C'];

export type allSonorityCats = [
  'V', 'C', 'O', 'Y', 'S',
  'Ɑ', 'Ɛ', 'Ʊ', 'U', 'J', 'Ɽ', 'Ɬ', 'X', 'N', 'F', 'A', 'D', 'T',
  'Ƃ', 'Ꜭ',
];
